import type { JsonObject } from "@arti/dashboard-events";

import { isRecord } from "@arti/utilities";

/**
 * Event summary extraction for provider progress display.
 *
 * Field names are derived from ArtiProviderEventDataMap in @arti/arti-provider.
 * The serialization pipeline preserves camelCase keys end-to-end —
 * no snake_case fallbacks are needed.
 */
import type { ProviderProgressEntry } from "../../../hooks/useProviderProgress";

interface EventSummaryAccessors {
  data: ProviderProgressEntry["event_data"];
  getArrayLength: (keys: string[]) => number | null;
  getNumber: (keys: string[]) => number | null;
  getString: (keys: string[]) => string | null;
}

function resolveAppliedLabel(changesApplied: unknown): string {
  if (typeof changesApplied !== "boolean") {
    return "";
  }
  return changesApplied ? " (changes applied)" : " (advisory)";
}

function asObject(value: unknown): JsonObject | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return Object.fromEntries(Object.entries(value)) as JsonObject;
}

function getContextStageCompletedSummary(
  artifactData: JsonObject | null,
  stage: string,
): string {
  switch (stage) {
    case "explore_artifact": {
      const count =
        artifactData && typeof artifactData.file_reference_count === "number"
          ? artifactData.file_reference_count
          : null;
      return typeof count === "number"
        ? `Explore artifact · ${count} refs`
        : "Explore artifact";
    }
    case "verification_seed": {
      const count =
        artifactData && Array.isArray(artifactData.criteria)
          ? artifactData.criteria.length
          : null;
      return typeof count === "number"
        ? `Verification seed · ${count} criteria`
        : "Verification seed";
    }
    case "verification_result": {
      const ready =
        artifactData && typeof artifactData.ready_for_completion === "boolean"
          ? artifactData.ready_for_completion
          : null;
      return ready === true
        ? "Verification result · ready"
        : "Verification result";
    }
    default: {
      return stage;
    }
  }
}

function getIndexingSubmissionSummary(
  event: ProviderProgressEntry,
  { getNumber }: Pick<EventSummaryAccessors, "getNumber">,
): string | null | undefined {
  switch (event.event_type) {
    case "indexing:repo-insights-submitted": {
      const count = getNumber(["insightCount"]);
      const facets = getNumber(["facetCount"]);
      if (typeof count !== "number") {
        return null;
      }
      return typeof facets === "number"
        ? `${count} insights · ${facets} facets`
        : `${count} insights`;
    }
    case "indexing:checklist-submitted": {
      const checked = getNumber(["checkedItems"]);
      const total = getNumber(["totalItems"]);
      if (typeof checked === "number" && typeof total === "number") {
        return `${checked}/${total} checked`;
      }
      const missing = getNumber(["missingCount"]);
      return typeof missing === "number" ? `${missing} missing` : null;
    }
    default: {
      return undefined;
    }
  }
}

function getEmbeddingSummary(
  event: ProviderProgressEntry,
  {
    getNumber,
    getString,
  }: Pick<EventSummaryAccessors, "getNumber" | "getString">,
): string | null | undefined {
  switch (event.event_type) {
    case "embedding:start": {
      const repo = getString(["repositorySlug"]);
      const model = getString(["model"]);
      return repo && model ? `${repo} · ${model}` : (repo ?? model);
    }
    case "embedding:progress": {
      const phase = getString(["phase"]);
      const chunksEmbedded = getNumber(["chunksEmbedded"]);
      const totalChunks = getNumber(["totalChunks"]);
      const filesDiscovered = getNumber(["filesDiscovered"]);
      if (
        phase === "embed" &&
        typeof chunksEmbedded === "number" &&
        typeof totalChunks === "number" &&
        totalChunks > 0
      ) {
        const pct = Math.round((chunksEmbedded / totalChunks) * 100);
        return `${chunksEmbedded}/${totalChunks} chunks (${pct}%)`;
      }
      if (phase === "discover") {
        return "Discovering files…";
      }
      if (phase === "chunk") {
        return typeof filesDiscovered === "number"
          ? `Chunking ${filesDiscovered} files…`
          : "Chunking files…";
      }
      if (phase === "store") {
        return "Storing embeddings…";
      }
      return phase ?? null;
    }
    case "embedding:complete": {
      const files = getNumber(["filesDiscovered"]);
      const chunks = getNumber(["totalChunks"]);
      const durationMs = getNumber(["durationMs"]);
      const parts: string[] = [];
      if (typeof files === "number") {
        parts.push(`${files} files`);
      }
      if (typeof chunks === "number") {
        parts.push(`${chunks} chunks`);
      }
      if (typeof durationMs === "number" && durationMs > 0) {
        parts.push(`${(durationMs / 1000).toFixed(1)}s`);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    default: {
      return undefined;
    }
  }
}

function getRunLifecycleAndStreamSummary(
  event: ProviderProgressEntry,
  { data, getArrayLength, getNumber, getString }: EventSummaryAccessors,
): string | null | undefined {
  switch (event.event_type) {
    case "run:start": {
      const mode = getString(["mode"]);
      const model = getString(["model"]);
      const phases = getArrayLength(["phases"]);
      const parts: string[] = [];
      if (mode) {
        parts.push(mode);
      }
      if (model) {
        parts.push(model);
      }
      if (typeof phases === "number") {
        parts.push(`${phases} phases`);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    case "run:end": {
      const result = getString(["result"]);
      const totalSteps = getNumber(["totalSteps"]);
      const duration = getNumber(["duration"]);
      const parts: string[] = [];
      if (result) {
        parts.push(result);
      }
      if (typeof totalSteps === "number") {
        parts.push(`${totalSteps} steps`);
      }
      if (typeof duration === "number" && duration > 0) {
        parts.push(`${(duration / 1000).toFixed(1)}s`);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    case "run:error": {
      const error = asObject(data.error);
      if (error && typeof error.message === "string") {
        return error.message;
      }
      return getString(["message"]);
    }
    case "stream:started": {
      const mode = getString(["mode"]);
      const model = getString(["model"]);
      if (mode && model) {
        return `${mode} · ${model}`;
      }
      return mode ?? model;
    }
    default: {
      return undefined;
    }
  }
}

function getToolAndCommandSummary(
  event: ProviderProgressEntry,
  { getNumber, getString }: EventSummaryAccessors,
): string | null | undefined {
  switch (event.event_type) {
    case "tool:start":
    case "tool:error": {
      return getString(["toolName"]);
    }
    case "tool:end": {
      const toolName = getString(["toolName"]);
      const duration = getNumber(["duration"]);
      const { success } = event.event_data;
      const parts: string[] = [];
      if (toolName) {
        parts.push(toolName);
      }
      if (typeof duration === "number") {
        parts.push(`${(duration / 1000).toFixed(1)}s`);
      }
      if (success === false) {
        parts.push("failed");
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    case "file:read":
    case "file:write":
    case "file:edit":
    case "file:delete": {
      return getString(["path"]);
    }
    case "command:start": {
      const command = getString(["command"]);
      const toolContext = getString(["toolContext"]);
      const truncatedCommand = command
        ? command.slice(0, 50) + (command.length > 50 ? "..." : "")
        : null;
      if (toolContext && truncatedCommand) {
        return `[${toolContext}] ${truncatedCommand}`;
      }
      return truncatedCommand;
    }
    case "command:end": {
      const command = getString(["command"]);
      const toolContext = getString(["toolContext"]);
      const exitCode = getNumber(["exitCode"]);
      const duration = getNumber(["duration"]);
      const truncatedCommand = command
        ? command.slice(0, 50) + (command.length > 50 ? "..." : "")
        : null;
      const parts: string[] = [];
      if (toolContext && truncatedCommand) {
        parts.push(`[${toolContext}] ${truncatedCommand}`);
      } else if (truncatedCommand) {
        parts.push(truncatedCommand);
      }
      if (typeof exitCode === "number" && exitCode !== 0) {
        parts.push(`exit ${exitCode}`);
      }
      if (typeof duration === "number") {
        parts.push(`${(duration / 1000).toFixed(1)}s`);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    default: {
      return undefined;
    }
  }
}

function getStepAndCompletionSummary(
  event: ProviderProgressEntry,
  { data, getNumber, getString }: EventSummaryAccessors,
): string | null | undefined {
  switch (event.event_type) {
    case "step:start": {
      const stepNumber = getNumber(["stepNumber"]);
      const model = getString(["model"]);
      if (typeof stepNumber === "number" && model) {
        return `Step ${stepNumber} · ${model}`;
      }
      return typeof stepNumber === "number" ? `Step ${stepNumber}` : null;
    }
    case "step:end": {
      const stepNumber = getNumber(["stepNumber"]);
      const finishReason = getString(["finishReason"]);
      const duration = getNumber(["duration"]);
      const parts: string[] = [];
      if (typeof stepNumber === "number") {
        parts.push(`Step ${stepNumber}`);
      }
      if (finishReason) {
        parts.push(finishReason);
      }
      if (typeof duration === "number") {
        parts.push(`${(duration / 1000).toFixed(1)}s`);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    case "completion:marked": {
      return getString(["summary"]);
    }
    case "completion:verified": {
      const { success, validationErrors } = data;
      if (success === true) {
        return "verified";
      }
      if (success === false) {
        const errorCount = Array.isArray(validationErrors)
          ? validationErrors.length
          : 0;
        return errorCount > 0
          ? `verification failed · ${errorCount} errors`
          : "verification failed";
      }
      return null;
    }
    default: {
      return undefined;
    }
  }
}

function getPhaseAndModelSummary(
  event: ProviderProgressEntry,
  { data, getString }: EventSummaryAccessors,
): string | null | undefined {
  switch (event.event_type) {
    case "phase:transition": {
      const from = getString(["from"]);
      const to = getString(["to"]);
      if (from && to) {
        return `${from} → ${to}`;
      }
      return to ? `→ ${to}` : null;
    }
    case "phase:transition-blocked": {
      const from = getString(["from"]);
      const to = getString(["to"]);
      const reason = getString(["reason"]);
      const transition = from && to ? `${from} → ${to}` : null;
      if (transition && reason) {
        return `${transition} · ${reason}`;
      }
      return reason ?? transition;
    }
    case "phase:status": {
      const phase = getString(["phase"]);
      const budget = asObject(data.budget);
      const percentUsed = budget?.percentUsed;
      const severity = budget?.severity;
      if (typeof percentUsed === "number") {
        const percent = Math.round(percentUsed);
        const severityLabel =
          typeof severity === "string" && severity !== "ok"
            ? `, ${severity}`
            : "";
        return `${phase} (${percent}% budget${severityLabel})`;
      }
      return phase;
    }
    case "model:selected": {
      const mainModel = getString(["mainModel"]);
      const exploringModel = getString(["exploringModel"]);
      if (mainModel && exploringModel) {
        return `${mainModel} · explore: ${exploringModel}`;
      }
      return mainModel;
    }
    case "model:switched": {
      const from = getString(["from"]);
      const to = getString(["to"]);
      const reason = getString(["reason"]);
      const parts: string[] = [];
      if (from && to) {
        parts.push(`${from} → ${to}`);
      } else if (to) {
        parts.push(to);
      }
      if (reason) {
        parts.push(reason);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    default: {
      return undefined;
    }
  }
}

function getTopologyAndPolicySummary(
  event: ProviderProgressEntry,
  {
    getNumber,
    getString,
  }: Pick<EventSummaryAccessors, "getNumber" | "getString">,
): string | null | undefined {
  switch (event.event_type) {
    case "topology:resolved": {
      const count = getNumber(["serviceCount"]);
      return typeof count === "number" ? `${count} services` : null;
    }
    case "topology:failed": {
      return getString(["error", "reason", "message"]);
    }
    case "topology:fallback": {
      return getString(["reason"]);
    }
    case "topology:resolving": {
      return getString(["target"]);
    }
    case "tool:policy-violation": {
      const toolName = getString(["toolName"]);
      const message = getString(["message"]);
      if (toolName && message) {
        return `${toolName} · ${message}`;
      }
      return toolName ?? message;
    }
    default: {
      return undefined;
    }
  }
}

function getPlanningAndReasoningSummary(
  event: ProviderProgressEntry,
  { getArrayLength, getNumber, getString }: EventSummaryAccessors,
): string | null | undefined {
  switch (event.event_type) {
    case "plan:approach": {
      const understanding = getString(["taskUnderstanding"]);
      const steps = getArrayLength(["implementationSteps"]);
      const validationErrors = getArrayLength(["validationErrors"]);
      const parts: string[] = [];
      if (understanding) {
        parts.push(understanding);
      }
      if (typeof steps === "number") {
        parts.push(`${steps} steps`);
      }
      if (
        event.event_data.planValidated === false &&
        typeof validationErrors === "number" &&
        validationErrors > 0
      ) {
        parts.push(`${validationErrors} validation errors`);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    case "progress:update": {
      const currentStep = getString(["currentStep"]);
      const progressPercent = getNumber(["progressPercent"]);
      const blockers = getArrayLength(["blockers"]);
      const parts: string[] = [];
      if (currentStep) {
        parts.push(currentStep);
      }
      if (typeof progressPercent === "number") {
        parts.push(`${progressPercent}%`);
      }
      if (typeof blockers === "number" && blockers > 0) {
        parts.push(`${blockers} blockers`);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    case "reasoning:note": {
      const text = getString(["text"]);
      if (!text) {
        return null;
      }
      return text.startsWith("Google grounded answer via ")
        ? text.replace(
            "Google grounded answer via ",
            "Google grounded search · ",
          )
        : text;
    }
    case "exploration:goals-defined":
    case "indexing:goals-defined": {
      const count = getNumber(["goalsCount"]);
      return typeof count === "number" ? `${count} goals` : null;
    }
    case "exploration:findings-submitted":
    case "indexing:findings-submitted": {
      const count = getNumber(["findingsCount"]);
      const status = getString(["status"]);
      const parts: string[] = [];
      if (typeof count === "number") {
        parts.push(`${count} findings`);
      }
      if (status && status !== "submitted") {
        parts.push(status);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    case "repo_chat:evidence-submitted": {
      const evidenceCount = getNumber(["evidenceCount"]);
      const consultedRepoCount = getNumber(["consultedRepoCount"]);
      if (
        typeof evidenceCount === "number" &&
        typeof consultedRepoCount === "number"
      ) {
        return `${evidenceCount} evidence · ${consultedRepoCount} repos`;
      }
      return typeof evidenceCount === "number"
        ? `${evidenceCount} evidence`
        : null;
    }
    default: {
      return undefined;
    }
  }
}

function getContextAndIndexingSummary(
  event: ProviderProgressEntry,
  { data, getNumber, getString }: EventSummaryAccessors,
): string | null | undefined {
  switch (event.event_type) {
    case "context:implementation-defined":
    case "context:review-defined": {
      const count = getNumber(["questionsCount"]);
      return typeof count === "number" ? `${count} questions` : null;
    }
    case "context:implementation-findings":
    case "context:review-findings": {
      const summary = getString(["summary"]);
      const count = getNumber(["findingsCount"]);
      const discoveredFiles = getNumber(["discoveredFilesCount"]);
      if (summary) {
        return summary;
      }
      const parts: string[] = [];
      if (typeof count === "number") {
        parts.push(`${count} findings`);
      }
      if (typeof discoveredFiles === "number") {
        parts.push(`${discoveredFiles} files discovered`);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    case "context:stage_completed": {
      const artifact = asObject(data.artifact);
      const stage =
        artifact && typeof artifact.stage === "string" ? artifact.stage : null;
      if (!stage) {
        return null;
      }
      const artifactData = artifact ? asObject(artifact.data) : null;
      return getContextStageCompletedSummary(artifactData, stage);
    }
    case "indexing:repo-insights-submitted":
    case "indexing:checklist-submitted": {
      return getIndexingSubmissionSummary(event, { getNumber });
    }
    case "embedding:start":
    case "embedding:progress":
    case "embedding:complete": {
      return getEmbeddingSummary(event, { getNumber, getString });
    }
    default: {
      return undefined;
    }
  }
}

function getExploreFindingsSummary(
  event: ProviderProgressEntry,
  { getNumber }: EventSummaryAccessors,
): string | null | undefined {
  if (event.event_type !== "explore:findings-submitted") {
    return undefined;
  }
  const fileRefs = getNumber(["fileReferenceCount"]);
  const patterns = getNumber(["patternCount"]);
  const risks = getNumber(["riskCount"]);
  const parts: string[] = [];
  if (typeof fileRefs === "number") {
    parts.push(`${fileRefs} file refs`);
  }
  if (typeof patterns === "number") {
    parts.push(`${patterns} patterns`);
  }
  if (typeof risks === "number" && risks > 0) {
    parts.push(`${risks} risks`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

function getPlanRevisionSummary(
  event: ProviderProgressEntry,
  accessors: EventSummaryAccessors,
): string | null | undefined {
  const { getNumber, getString } = accessors;
  switch (event.event_type) {
    case "plan:revision-intent": {
      const intent = getString(["intent"]);
      const scope = getString(["scope"]);
      if (intent && scope) {
        return `${intent} · ${scope}`;
      }
      return intent;
    }
    case "plan:revision-repo-selection": {
      return getRevisionRepoSelectionSummary(accessors);
    }
    case "repos:listed": {
      const count = getNumber(["repoCount"]);
      return typeof count === "number" ? `${count} repositories` : null;
    }
    case "repo:insights": {
      const slug = getString(["repoSlug"]);
      const count = getNumber(["insightCount"]);
      if (slug && typeof count === "number" && count > 0) {
        return `${slug} · ${count} insights`;
      }
      return slug ? `${slug} · no insights` : null;
    }
    case "repo:branches": {
      const slug = getString(["repoSlug"]);
      const count = getNumber(["branchCount"]);
      if (slug && typeof count === "number") {
        return `${slug} · ${count} branches`;
      }
      return slug;
    }
    case "repo:submodules": {
      const slug = getString(["repoSlug"]);
      const branch = getString(["branch"]);
      const count = getNumber(["submoduleCount"]);
      if (slug && branch && typeof count === "number") {
        return `${slug}@${branch} · ${count} submodules`;
      }
      return slug;
    }
    case "plan:revision-extensive": {
      const unitsOfWork = getNumber(["unitsOfWorkCount"]);
      const complexity = getString(["planComplexityScore"]);
      const status = getString(["status"]);
      const parts: string[] = [];
      if (typeof unitsOfWork === "number") {
        parts.push(`${unitsOfWork} units of work`);
      }
      if (complexity) {
        parts.push(complexity);
      }
      if (status === "validation_failed") {
        parts.push("validation failed");
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    case "plan:revision-subtasks": {
      return getRevisionSubtasksSummary(accessors);
    }
    case "plan:revision-touchpoints": {
      const count = getNumber(["touchpointsCount"]);
      return typeof count === "number" ? `${count} touchpoints` : null;
    }
    default: {
      return undefined;
    }
  }
}

function getDiscoveryAndSearchSummary(
  event: ProviderProgressEntry,
  { getNumber, getString }: EventSummaryAccessors,
): string | null | undefined {
  switch (event.event_type) {
    case "search:code": {
      const query = getString(["query"]);
      const matchCount = getNumber(["matchCount"]);
      if (query && typeof matchCount === "number") {
        return `${query} · ${matchCount} matches`;
      }
      return query;
    }
    case "directory:list": {
      return getString(["path"]);
    }
    case "projects:list": {
      const count = getNumber(["projectCount"]);
      return typeof count === "number" ? `${count} projects` : null;
    }
    case "file:find": {
      return getString(["pattern"]);
    }
    case "symbol:info": {
      return getString(["symbol"]);
    }
    case "semantic:search-pattern":
    case "semantic:find-file":
    case "semantic:find-symbol":
    case "semantic:get-symbols":
    case "semantic:find-references": {
      return getString(["project"]);
    }
    default: {
      return undefined;
    }
  }
}

function getReviewAndDebugSummary(
  event: ProviderProgressEntry,
  { data, getArrayLength, getNumber, getString }: EventSummaryAccessors,
): string | null | undefined {
  switch (event.event_type) {
    case "review:request": {
      const attempt = getNumber(["attempt"]);
      const patchBytes = getNumber(["patchBytes"]);
      const filesChanged = getArrayLength(["filesChanged"]);
      const parts: string[] = [];
      if (typeof attempt === "number") {
        parts.push(`attempt ${attempt}`);
      }
      if (typeof filesChanged === "number") {
        parts.push(`${filesChanged} files`);
      }
      if (typeof patchBytes === "number") {
        parts.push(`${patchBytes} bytes`);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    case "review:response": {
      const category = getString(["category"]);
      const message = getString(["message"]);
      const issueCount = getArrayLength(["issues"]);
      const parts: string[] = [];
      if (category) {
        parts.push(category.replaceAll("_", " "));
      }
      if (typeof issueCount === "number" && issueCount > 0) {
        parts.push(`${issueCount} issues`);
      }
      if (!category && message) {
        parts.push(message);
      }
      return parts.length > 0 ? parts.join(" · ") : null;
    }
    case "ui_review:request": {
      const attempt = getNumber(["attempt"]);
      const patchBytes = getNumber(["patchBytes"]);
      if (typeof patchBytes === "number") {
        return `patch ${patchBytes} bytes`;
      }
      return typeof attempt === "number" ? `attempt ${attempt}` : null;
    }
    case "ui_review:response": {
      const status = getString(["status"]);
      const { requiresRemedy, changesApplied } = data;
      if (typeof requiresRemedy === "boolean" && status) {
        const appliedLabel = resolveAppliedLabel(changesApplied);
        return `${status}${requiresRemedy ? " (remedy required)" : appliedLabel}`;
      }
      if (typeof requiresRemedy === "boolean") {
        return requiresRemedy ? "remedy required" : "no remedy required";
      }
      return status;
    }
    case "usage:update": {
      return getString(["modelRef"]);
    }
    case "prompt_cache:break": {
      const breakReason = getString(["breakReason"]);
      const cachedTokens = getNumber(["stepCachedTokens"]);
      const inputTokens = getNumber(["stepInputTokens"]);

      const tokenSummary =
        typeof cachedTokens === "number" && typeof inputTokens === "number"
          ? `${cachedTokens}/${inputTokens} cached`
          : null;

      const reasonLabel = breakReason?.replaceAll("_", " ") ?? null;
      if (reasonLabel && tokenSummary) {
        return `${reasonLabel} · ${tokenSummary}`;
      }
      if (reasonLabel) {
        return reasonLabel;
      }
      return tokenSummary;
    }
    case "debug:message": {
      return getString(["message"]);
    }
    case "message:injected": {
      return getString(["message"]);
    }
    default: {
      return undefined;
    }
  }
}

export function getEventSummary(event: ProviderProgressEntry): string | null {
  const data = event.event_data;

  const accessors: EventSummaryAccessors = {
    data,
    getArrayLength: (keys) => {
      for (const key of keys) {
        const value = data[key];
        if (Array.isArray(value)) {
          return value.length;
        }
      }
      return null;
    },
    getNumber: (keys) => {
      for (const key of keys) {
        const value = data[key];
        if (typeof value === "number") {
          return value;
        }
      }
      return null;
    },
    getString: (keys) => {
      for (const key of keys) {
        const value = data[key];
        if (typeof value === "string" && value.trim()) {
          return value;
        }
      }
      return null;
    },
  };

  const resolvers = [
    getRunLifecycleAndStreamSummary,
    getToolAndCommandSummary,
    getStepAndCompletionSummary,
    getPhaseAndModelSummary,
    getPlanningAndReasoningSummary,
    getExploreFindingsSummary,
    getContextAndIndexingSummary,
    getPlanRevisionSummary,
    getDiscoveryAndSearchSummary,
    getTopologyAndPolicySummary,
    getReviewAndDebugSummary,
  ] as const;

  for (const resolveSummary of resolvers) {
    const summary = resolveSummary(event, accessors);
    if (summary !== undefined) {
      return summary;
    }
  }

  return null;
}

function getRevisionRepoSelectionSummary({
  data,
  getNumber,
}: EventSummaryAccessors) {
  const { selections } = data;
  if (Array.isArray(selections) && selections.length > 0) {
    const formatted = selections
      .flatMap((entry) => {
        if (!isRecord(entry)) {
          return [];
        }
        const record = entry;
        if (typeof record.slug !== "string") {
          return [];
        }
        return [
          typeof record.branch === "string"
            ? `${record.slug}@${record.branch}`
            : record.slug,
        ];
      })
      .join(", ");
    if (formatted) {
      return formatted;
    }
  }
  const count = getNumber(["selectedReposCount"]);
  const branches = getNumber(["selectedBranchesCount"]);
  if (typeof count === "number" && typeof branches === "number") {
    return `${count} repos · ${branches} branches`;
  }
  return typeof count === "number" ? `${count} repos` : null;
}

function getRevisionSubtasksSummary({
  data,
  getNumber,
  getString,
}: EventSummaryAccessors) {
  const count = getNumber(["subtasksCount"]);
  const status = getString(["status"]);
  const diffSummary = asObject(data.diffSummary);
  const parts: string[] = [];
  if (typeof count === "number") {
    parts.push(`${count} subtasks`);
  }
  if (diffSummary) {
    const { added, removed, updated } = diffSummary;
    const diffs: string[] = [];
    if (typeof added === "number" && added > 0) {
      diffs.push(`+${added}`);
    }
    if (typeof removed === "number" && removed > 0) {
      diffs.push(`-${removed}`);
    }
    if (typeof updated === "number" && updated > 0) {
      diffs.push(`~${updated}`);
    }
    if (diffs.length > 0) {
      parts.push(diffs.join(" "));
    }
  }
  if (status === "validation_failed") {
    parts.push("validation failed");
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}
